import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Skull } from 'lucide-react';
import NoiseOverlay from '../components/ui/NoiseOverlay';
import Marquee from '../components/ui/Marquee';
import RealmTextBg from '../components/ui/RealmTextBg';
import ProductCard from '../components/ui/ProductCard';
import ComingSoon from '../components/ComingSoon';
import { THEMES, isValidRealm } from '../lib/themes';
import api from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { trackPageView, trackAddToCart } from '../utils/analytics';
import { collectProductImageUrls } from '../lib/images';

// Solid realm background colors (for the SVG title knockout tint)
const REALM_HEX = {
  anime: '#7f1d1d',
  tantra: '#171717',
  inkspeak: '#f0e6d2',
  soul: '#e0e7ff',
};

const CategoryPage = () => {
  const { realm } = useParams();
  const { addItem, openDrawer } = useCart();
  const { push: pushToast } = useToast();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState('All');
  const [error, setError] = useState(null);

  const theme = THEMES[realm];
  const realmHex = REALM_HEX[realm] || '#fde047';
  const titleMaskId = `rt-title-mask-${realm}`;

  // Reset state synchronously when the realm changes, so the previous realm's
  // products never flash for a frame before the new fetch resolves.
  const [activeRealm, setActiveRealm] = useState(realm);
  if (realm !== activeRealm) {
    setActiveRealm(realm);
    setProducts([]);
    setError(null);
    setSelectedSub('All');
    setLoading(true);
  }

  useEffect(() => {
    if (!realm) return;
    trackPageView(`category/${realm}`, realm);
  }, [realm]);

  useEffect(() => {
    let cancelled = false;
    if (!realm || !isValidRealm(realm)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await api.get(`/products?category=${realm}`);
        if (cancelled) return;
        if (res.data?.success) {
          setProducts((res.data.data || []).filter((p) => p.isActive));
        } else {
          setProducts([]);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load products');
        setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [realm]);

  const subCategories = useMemo(() => {
    const types = Array.from(new Set(products.map((p) => p.type).filter(Boolean)));
    return ['All', ...types];
  }, [products]);

  if (!realm || !isValidRealm(realm)) return <Navigate to="/" replace />;

  // Clean themed loader while fetching — avoids flashing placeholders/header
  // before resolving to either the grid or the Coming Soon screen.
  if (loading) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${theme.colors.bg} ${theme.font} relative overflow-hidden`}>
        <NoiseOverlay />
        <div className={`animate-spin-slow ${theme.colors.text}`}>
          {React.cloneElement(theme.icon, { size: 64 })}
        </div>
        <div className="mt-6 bg-black text-white px-5 py-2 font-black uppercase tracking-[0.3em] text-sm">
          Loading {theme.label}
        </div>
      </div>
    );
  }

  // Coming-soon if the realm has zero live products at all
  const liveCount = products.filter((p) => p.inStock).length;
  if (liveCount === 0 && !error) {
    return <ComingSoon theme={realm} onBack={() => (window.location.href = '/')} />;
  }

  const filtered = selectedSub === 'All'
    ? products
    : products.filter((p) => p.type === selectedSub);

  const handleQuickAdd = (product) => {
    const defaultSize = product.sizes?.[0]?.size || 'L';
    addItem(product, { size: defaultSize, quantity: 1 });
    trackAddToCart(product._id || product.id, { name: product.name, price: product.price });
    pushToast(`Added ${product.name}`, { tone: 'success' });
    openDrawer();
  };

  const handlePreload = (product) => {
    const urls = collectProductImageUrls(product);
    urls.forEach((u) => {
      const img = new Image();
      img.src = u;
    });
  };

  const getMarqueeTextColor = (id) =>
    id === 'inkspeak' ? 'text-white' : id === 'tantra' ? 'text-white' : 'text-black';

  return (
    <div className={`min-h-screen transition-colors duration-700 ${theme.colors.bg} ${theme.font} relative overflow-x-hidden`}>
      <NoiseOverlay />

      <RealmTextBg theme={theme} />

      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute bottom-10 left-10 opacity-20 animate-pulse">{theme.icon}</div>
        <div className="absolute top-20 right-20 opacity-20 animate-bounce delay-700">{theme.icon}</div>
      </div>

      <header className={`relative min-h-[78vh] flex items-center justify-center overflow-hidden ${theme.colors.bg}`}>
        {/* Video layer */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover kenburns z-0"
        >
          <source src="https://res.cloudinary.com/dts9ixkzr/video/upload/v1771080697/gg12_fqvkxd.webm" type="video/webm" />
        </video>

        {/* Realm-color tint with the title knocked out — the clip plays inside the letters.
            Desktop only: the "slice" scaling crops the wide title on narrow phones. */}
        <svg
          className="absolute inset-0 w-full h-full z-[1] hidden md:block"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 1200 700"
          aria-hidden="true"
        >
          <defs>
            <mask id={titleMaskId}>
              <rect width="1200" height="700" fill="white" />
              <text
                x="600" y="365" textAnchor="middle" dominantBaseline="middle"
                fontFamily="system-ui, Arial, sans-serif" fontWeight="900"
                fontSize="300" textLength="1080" lengthAdjust="spacingAndGlyphs"
                letterSpacing="-8" fill="black"
              >
                {theme.label}
              </text>
            </mask>
          </defs>
          <rect width="1200" height="700" fill={realmHex} fillOpacity="0.7" mask={`url(#${titleMaskId})`} />
          {/* white outline around the letters */}
          <text
            x="600" y="365" textAnchor="middle" dominantBaseline="middle"
            fontFamily="system-ui, Arial, sans-serif" fontWeight="900"
            fontSize="300" textLength="1080" lengthAdjust="spacingAndGlyphs"
            letterSpacing="-8" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="3"
          >
            {theme.label}
          </text>
        </svg>

        {/* Mobile: portrait-tuned knockout. Tall viewBox (aspect ~0.46) so "slice" crops
            vertically instead of horizontally — the full word stays visible on phones. */}
        <svg
          className="md:hidden absolute inset-0 w-full h-full z-[1]"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 600 1300"
          aria-hidden="true"
        >
          <defs>
            <mask id={`${titleMaskId}-m`}>
              <rect width="600" height="1300" fill="white" />
              <text
                x="300" y="655" textAnchor="middle" dominantBaseline="middle"
                fontFamily="system-ui, Arial, sans-serif" fontWeight="900"
                fontSize="150" textLength="500" lengthAdjust="spacingAndGlyphs"
                letterSpacing="-4" fill="black"
              >
                {theme.label}
              </text>
            </mask>
          </defs>
          <rect width="600" height="1300" fill={realmHex} fillOpacity="0.7" mask={`url(#${titleMaskId}-m)`} />
          <text
            x="300" y="655" textAnchor="middle" dominantBaseline="middle"
            fontFamily="system-ui, Arial, sans-serif" fontWeight="900"
            fontSize="150" textLength="500" lengthAdjust="spacingAndGlyphs"
            letterSpacing="-4" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2.5"
          >
            {theme.label}
          </text>
        </svg>

        {/* Edge vignette + fades that match the overlay tint for a seamless blend */}
        <div className="absolute inset-0 z-[2] pointer-events-none" style={{ boxShadow: 'inset 0 0 160px 40px rgba(0,0,0,0.35)' }} />
        <div
          className="absolute top-0 inset-x-0 h-28 z-[2] pointer-events-none"
          style={{ background: `linear-gradient(to bottom, ${realmHex}, transparent)` }}
        />
        <div
          className="absolute bottom-0 inset-x-0 h-36 z-[2] pointer-events-none"
          style={{ background: `linear-gradient(to top, ${realmHex}, transparent)` }}
        />

        {/* Stickers */}
        <div className="absolute top-10 left-10 rotate-[-12deg] border-4 border-black bg-white text-black px-4 py-2 font-black text-2xl shadow-[4px_4px_0px_black] hidden md:block z-20">
          {theme.sticker}
        </div>
        <div className="absolute bottom-10 right-10 rotate-[12deg] bg-black text-white px-6 py-3 font-mono font-bold border-2 border-white shadow-[4px_4px_0px_rgba(0,0,0,0.5)] hidden md:block z-20">
          EST. 2024
        </div>
        <div className={`absolute top-12 right-12 rotate-[8deg] ${theme.colors.accent} text-white px-4 py-2 font-black text-lg uppercase border-4 border-white shadow-[4px_4px_0px_rgba(0,0,0,0.6)] hidden md:block z-20`}>
          {theme.decorations?.[0] || 'NEW DROP'}
        </div>
        <div className="absolute bottom-12 left-12 -rotate-[6deg] bg-white text-black px-4 py-2 font-mono font-bold text-sm uppercase border-2 border-black shadow-[4px_4px_0px_rgba(0,0,0,0.4)] hidden md:block z-20">
          {theme.decorations?.[2] || 'VOL.01'}
        </div>

        {/* Eyebrow */}
        <span className="absolute top-[14%] left-1/2 -translate-x-1/2 z-10 text-white font-mono text-xs md:text-sm uppercase tracking-[0.35em] drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
          OWLFIT // Realm
        </span>

        {/* Caption */}
        <div className="absolute bottom-[15%] left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
          <div className="bg-black text-white px-6 py-2 font-bold text-lg md:text-xl uppercase tracking-widest transform rotate-1 shadow-xl">
            {theme.label} COLLECTION
          </div>
          <span className="text-white font-bold text-sm md:text-base uppercase tracking-[0.2em] drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
            {theme.vibe}
          </span>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center text-white/80">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] mb-1">Scroll</span>
          <div className="scroll-cue w-5 h-8 border-2 border-white/70 rounded-full flex items-start justify-center p-1">
            <span className="w-1 h-2 bg-white rounded-full" />
          </div>
        </div>
      </header>

      <Marquee
        text={`${theme.label} /// NEW ARRIVALS /// ${theme.vibe} /// `}
        className={`relative z-10 border-b-4 ${theme.colors.border} ${theme.colors.accent} ${getMarqueeTextColor(theme.id)} font-black text-xl py-3`}
        speed="10s"
      />

      {subCategories.length > 1 && (
        <div className="flex justify-center flex-wrap gap-3 md:gap-4 p-8 md:p-12 relative z-20">
          {subCategories.map((sub) => (
            <button
              key={sub}
              onClick={() => setSelectedSub(sub)}
              className={`px-6 md:px-8 py-3 md:py-4 text-base md:text-xl font-black border-4 transition-all duration-200 uppercase tracking-wide ${
                selectedSub === sub
                  ? 'bg-black text-white border-black shadow-[8px_8px_0px_0px_rgba(255,255,255,0.5)] -translate-y-2'
                  : 'bg-white text-black border-black hover:-translate-y-2 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]'
              } ${theme.shape}`}
            >
              {sub}
            </button>
          ))}
        </div>
      )}

      <main className="max-w-7xl mx-auto p-6 pb-32 relative z-10">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 border-4 border-black bg-yellow-300 shadow-[16px_16px_0px_black] transform rotate-1 mx-4">
            <div className="animate-spin-slow mb-6">
              <Skull size={80} strokeWidth={2.5} />
            </div>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4 text-center">
              {error ? 'CONNECTION ERROR' : 'COMING SOON'}
            </h2>
            <div className="bg-black text-white px-6 py-2 font-mono font-bold text-xl uppercase tracking-widest border-2 border-white shadow-[4px_4px_0px_rgba(0,0,0,0.2)]">
              {error ? 'Retry shortly' : 'Constructing New Drip...'}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filtered.map((product) => (
              <ProductCard
                key={product._id || product.id}
                product={product}
                theme={theme}
                onAddToCart={handleQuickAdd}
                onMouseEnter={() => handlePreload(product)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CategoryPage;
